import { useEffect, useState } from 'react';
import { authFetch } from '../utils/authFetch';
import { processCategoriesData, processSpecialtiesData } from '../utils/categories';

// Owns the data-layer state of the app: the services / practitioners
// split (`data`), `categories`, `documents`, `documentCategories`,
// `recentlyViewed`, `featuredDocumentId`, `dataLoading`,
// `appSalesTaxRate`, `includeSalesTax`. Also owns the initial
// `/api/data` fetch, which normalizes incoming records (practitioner
// schedules, offered-services shape, document statuses) and then hands
// the raw json + processed categories back to the parent via
// `onHydrated` so display-layer state + `mainBgColor` can seed too.
//
// Data-mutating handlers owned: `handleRestore` (archived item →
// active), `handleDeleteDocument`, `handleFeatureDocument`, and the
// four document-acknowledgement handlers (`appendAcknowledgement`,
// `handleAcknowledge`, `handleSaveSignature`,
// `handleDeleteAcknowledgement`).
//
// Modal-tangled handlers (`handleModalSave`, `handleArchive`,
// `handleDelete`) deliberately stay in the parent — their dep graph
// reaches deep into the modal UI state (modalType / modalForm /
// sopModalForm / setModalVisible / setShowConfirmModal / selection
// state / cross-hook display setters) and moving them would either
// balloon this hook's input list or require first extracting a
// `useModals` hook. Tracked for a future phase.
//
// Handlers are declared as plain functions (no useCallback) because
// no consumer memoizes on stable identity and several of them close
// over the current `documents` / `data` slices — the pre-refactor
// behavior we're preserving. They follow the original "stale
// getAllData() + fresh updatedState override" pattern: setState
// synchronously, then call `onSave(updatedState)` which the parent
// expands into `handleSave({ ...getAllData(), ...updatedState })`.
// The stale slice is harmless because `updatedState` always wins.
export function useAppData({
  defaults,
  onHydrated,
  onSave,
  playClickSound,
  ackName,
  setAckName,
  setIsSigning,
  setViewingDocument,
  setShowConfirmModal,
  setSaveStatus,
  onLoadError,
}) {
  const [data, setData] = useState({ services: [], practitioners: [] });
  const [dataLoading, setDataLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  // Practitioner specialties ({ name, category, color }). Seeded from
  // DEFAULT_SPECIALTIES until the core doc carries its own list.
  const [specialties, setSpecialties] = useState(() => processSpecialtiesData(null, defaults.categoryColor));
  const [documents, setDocuments] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [featuredDocumentId, setFeaturedDocumentId] = useState(null);
  const [appSalesTaxRate, setAppSalesTaxRate] = useState(0.08875);
  const [includeSalesTax, setIncludeSalesTax] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        let json;
        if (window.ipcRenderer) {
          json = await window.ipcRenderer.invoke('data');
        } else {
          const response = await fetch('/api/data');
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          json = await response.json();
        }
        if (!alive) return;
        if (!json) { setDataLoading(false); return; }

        const services = Array.isArray(json.services) ? json.services : [];
        const processedCategories = processCategoriesData(json.categories, services, defaults.categoryColor);

        const defaultSchedule = defaults.DAYS.reduce((acc, day) => ({ ...acc, [day]: 'OFF' }), {});
        const processedPractitioners = Array.isArray(json.practitioners)
          ? json.practitioners.map((p) => ({
              ...p,
              offered_services: Array.isArray(p.offered_services)
                ? p.offered_services.map((os) => ({
                    ...os,
                    offeredDurations: Array.isArray(os.offeredDurations) ? os.offeredDurations : [],
                  }))
                : [],
              bedHeight: p.bedHeight || 3,
              days_working: p.days_working || defaultSchedule,
            }))
          : [];

        const rawDocuments = Array.isArray(json.documents) ? json.documents : [];
        const rawDocumentCategories = Array.isArray(json.documentCategories) ? json.documentCategories : [];
        const normalizedDocuments = rawDocuments.map((d) => {
          const statuses = Array.isArray(d.statuses) ? d.statuses : (d.status ? [d.status] : []);
          const primary = statuses.length > 0 ? statuses[0] : (d.status || '');
          const isArchived = d.isArchived || statuses.includes('Expired') || d.status === 'Expired';
          return { ...d, statuses, status: primary, isArchived };
        });

        setCategories(processedCategories);
        setSpecialties(processSpecialtiesData(json.specialties, defaults.categoryColor));
        setDocuments(normalizedDocuments);
        setDocumentCategories(rawDocumentCategories);
        setRecentlyViewed(Array.isArray(json.recentlyViewed) ? json.recentlyViewed : []);
        setFeaturedDocumentId(json.featuredDocumentId || null);
        setData({ services, practitioners: processedPractitioners });

        if (typeof json.salesTaxRate === 'number') setAppSalesTaxRate(json.salesTaxRate);

        if (typeof onHydrated === 'function') {
          onHydrated({ json, processedCategories });
        }

        setDataLoading(false);
      } catch (err) {
        if (!alive) return;
        if (typeof onLoadError === 'function') onLoadError(err);
        setDataLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRestore = async (item, type) => {
    playClickSound();
    let updatedState = {};
    if (type === 'service') {
      const updatedServices = data.services.map((s) => s.name === item.name ? { ...s, isArchived: false } : s);
      setData((d) => ({ ...d, services: updatedServices }));
      updatedState = { services: updatedServices };
    } else if (type === 'practitioner') {
      const updatedPractitioners = data.practitioners.map((p) => p.name === item.name ? { ...p, isArchived: false } : p);
      setData((d) => ({ ...d, practitioners: updatedPractitioners }));
      updatedState = { practitioners: updatedPractitioners };
    }
    await onSave(updatedState);
  };

  const handleDeleteDocument = async (id) => {
    const docToDelete = documents.find((d) => d.id === id);
    if (!docToDelete) return;

    if (window.electronAPI) {
      try {
        const result = await window.electronAPI.deleteDocument(docToDelete.filename);
        if (result.error) throw new Error(result.error);
      } catch (error) {
        console.error('Delete error:', error);
        setSaveStatus(`Delete failed: ${error.message}`);
        setTimeout(() => setSaveStatus(''), 5000);
      }
    }

    const newDocuments = documents.filter((d) => d.id !== id);
    setDocuments(newDocuments);
    await onSave({ documents: newDocuments });
    setShowConfirmModal(false);
  };

  const handleFeatureDocument = async (docId) => {
    playClickSound();
    setFeaturedDocumentId(docId);
    await onSave({ featuredDocumentId: docId });
  };

  // Append an acknowledgement via POST /api/save with
  // `action: 'acknowledge'`. That action path accepts both admin and
  // frontdesk scope (front-desk tokens can't do the default full-save
  // path) and uses a Firestore transaction to append to one document's
  // acknowledgements array without touching the rest of the core doc.
  // Folded into /api/save to stay under Vercel Hobby's 12-function cap.
  // Server owns id + timestamp generation; response includes the
  // updated document which we splice into local state so the viewer's
  // history list reflects the save immediately.
  const appendAcknowledgement = async (docId) => {
    const res = await authFetch('/api/save', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'acknowledge', docId, name: ackName.trim() }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.ok) {
      const msg = (json && json.error) || 'Failed to save acknowledgement';
      throw new Error(msg);
    }
    const updatedDoc = json.document;
    setDocuments((prev) => prev.map((d) => (d.id === docId ? updatedDoc : d)));
    setViewingDocument((prev) => (prev && prev.id === docId ? updatedDoc : prev));
  };

  const handleAcknowledge = async (docId) => {
    if (!ackName.trim()) { alert('Please enter your name.'); return; }
    try {
      await appendAcknowledgement(docId);
      setAckName('');
    } catch (err) {
      console.error('Acknowledge error', err);
      alert(err.message || 'Could not save acknowledgement.');
    }
  };

  const handleSaveSignature = async (docId) => {
    if (!ackName.trim()) { alert('Please enter your name.'); return; }
    try {
      await appendAcknowledgement(docId);
      setAckName('');
      setIsSigning(false);
    } catch (err) {
      console.error('Signature save error', err);
      alert(err.message || 'Could not save signature.');
    }
  };

  // Admin-only full-save path: the scoped `action: 'acknowledge'`
  // endpoint only appends; deleting a row still requires the full
  // /api/save payload.
  const handleDeleteAcknowledgement = async (docId, ackId) => {
    const docToUpdate = documents.find((doc) => doc.id === docId);
    if (!docToUpdate) return;

    const updatedAcknowledgements = (docToUpdate.acknowledgements || []).filter((ack) => ack.id !== ackId);
    const updatedDocuments = documents.map((doc) =>
      doc.id === docId ? { ...doc, acknowledgements: updatedAcknowledgements } : doc
    );

    setDocuments(updatedDocuments);
    const newlyUpdatedDoc = updatedDocuments.find((doc) => doc.id === docId);
    if (newlyUpdatedDoc) setViewingDocument(newlyUpdatedDoc);
    await onSave({ documents: updatedDocuments });
  };

  const getSlice = () => ({
    categories,
    specialties,
    services: data.services,
    practitioners: data.practitioners,
    documents,
    documentCategories,
    recentlyViewed,
    featuredDocumentId,
    salesTaxRate: appSalesTaxRate,
  });

  return {
    data, setData,
    dataLoading,
    categories, setCategories,
    specialties, setSpecialties,
    documents, setDocuments,
    documentCategories, setDocumentCategories,
    recentlyViewed, setRecentlyViewed,
    featuredDocumentId, setFeaturedDocumentId,
    appSalesTaxRate, setAppSalesTaxRate,
    includeSalesTax, setIncludeSalesTax,
    handleRestore,
    handleDeleteDocument,
    handleFeatureDocument,
    appendAcknowledgement,
    handleAcknowledge,
    handleSaveSignature,
    handleDeleteAcknowledgement,
    getSlice,
  };
}
